import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { visuallyHidden } from "@mui/utils";
import { TOUCH_TARGET, touchTargetQuery } from "_shared/_views/touch-target";
import { useCallback, useState, type MouseEvent, type ReactNode } from "react";
import { Link as RouterLink, type To } from "react-router";

/**
 * One thing a row can have done to it. `label` is the verb, in the imperative — it is the tooltip
 * when the action is an icon and the menu text when it is not, so it has to read correctly in both.
 */
export type RowAction = {
  key: string;
  label: string;
  icon: ReactNode;
  /**
   * Handed the element a popover the action opens should hang from: the action's own button, or — once
   * the actions have folded into a menu, which closes as the item is pressed — the menu's button.
   */
  onClick: (anchor: HTMLElement) => void;
  /**
   * Marks an action that destroys something — delete, cancel, remove — and colours it accordingly.
   *
   * It is the only action that gets a colour, and that is the point: colour as a signal works while
   * it is scarce. Every icon in the app is a `SvgIcon`, whose default `color="inherit"` takes the
   * colour of the button around it, so the actions carry no colour of their own and none should be
   * passed. See [table row actions](../../../../../docs/software-design/table-row-actions.md).
   */
  destructive?: boolean;
  /**
   * Keeps the action in the menu even where there is room for its icon, so a row with many actions
   * shows one or two icons and a menu rather than a strip of four or five. The actions without it stay
   * icons beside the menu until the row collapses, when everything goes into the menu as usual.
   */
  inMenu?: boolean;
  disabled?: boolean;
  /** Replaces `label` while `disabled`, to say why rather than leave the user guessing. */
  disabledReason?: string;
  /**
   * Accessible name, where the visible label is not enough on its own. A column of buttons all
   * called "Delete" tells a screen reader nothing about which row it is on, so name the row here —
   * "Delete Acme Corp" — and leave `label` as the verb the sighted user reads. Defaults to `label`.
   */
  ariaLabel?: string;
};

const RowActionsContainer = styled("div")({
  display: "flex",
  flexWrap: "nowrap",
  // The cell's own `align` cannot reach these: a block-level flex container fills the cell and packs
  // its buttons at the start whatever the cell says about text alignment.
  justifyContent: "center",
  whiteSpace: "nowrap",
});

/**
 * MUI's default `IconButton` is 40px, and a menu item on a wide screen is 36px: both short of a
 * fingertip. They grow only on a phone or under a coarse pointer, so a mouse keeps the dense table.
 */
const ActionButton = styled(IconButton)(({ theme }) => ({
  [touchTargetQuery(theme)]: { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET },
}));

const ActionMenuItem = styled(MenuItem)(({ theme }) => ({
  [touchTargetQuery(theme)]: { minHeight: TOUCH_TARGET },
}));

const HiddenHeaderLabel = styled("span")({ ...visuallyHidden });

/**
 * Where a row opens, for the Open item that heads its ⋯ menu (#6711). `to` where the row navigates, so
 * the item is a real link that a middle-click opens in a new tab; `onClick` where it opens a dialog.
 */
export type RowOpenAction = {
  to?: To;
  onClick?: () => void;
  /** The item's text, where "Open" is not the whole of it: "Open request" on a grouped row. */
  label?: string;
};

/**
 * The actions column's header: named for screen readers, invisible to everyone else.
 *
 * It is the only header in a table that does nothing — it cannot be sorted, filtered, or reordered,
 * and it opens no column menu — so it is inert text where every other header is a control. The
 * row-select column has never had a visible one for the same reason. Hiding it is mostly about
 * saying the same thing on every page: it was shown on fifteen tables and hidden on two.
 *
 * It is not where the column's width goes, despite appearances. Measured on the team templates table
 * at 820px, hiding the label moved the column from 73px to 71px; the 24px of cell padding around a
 * 40px kebab is the rest. Narrowing that padding is the lever that actually buys width, and it is a
 * separate decision from this one.
 *
 * `display: none` would also hide the label, which is what two tables were doing, but that takes the
 * column's name away from a screen reader navigating cell by cell. Hiding it visually keeps it.
 */
export function RowActionsColumnHeader() {
  return <HiddenHeaderLabel>Actions</HiddenHeaderLabel>;
}

function describeAction(action: RowAction): string {
  return action.disabled && action.disabledReason ? action.disabledReason : action.label;
}

/**
 * The actions column for a table row: icons while there is room for them, one menu once there is not.
 * Actions marked `inMenu` sit in the menu even while there is room, beside the icons of the rest.
 *
 * Every table was rendering its own icons and none of them gave the width back when the table got
 * narrow, so three or four pinned icons ate the name column at high display scaling — the same
 * squeeze as #4964, except an actions column cannot be dropped the way a data column can. The
 * collapse is the part worth sharing: it was written once, on the communications page, and nowhere
 * else.
 *
 * A row that opens something passes `open`, and then a ⋯ ends the row at every width, with **Open**
 * first (#6711). Some readers never think to click a row, and the ⋯ is where they look for what a row
 * can do; the name drawn as a link (`RowLink`) and the row's own hover are the other two ways in.
 *
 * The breakpoint is the viewport by default, which is the right question for a table that fills a
 * page. It is the wrong question inside a proposal pane — the navigation drawer and the comments
 * panel both take width the window cannot see — so those tables measure their own container and pass
 * `collapsed` themselves. See `useWorkPaneNarrowerThan`.
 */
export default function RowActions(
  props: Readonly<{
    actions: readonly RowAction[];
    /** Override the viewport test where the window is not the width that matters. */
    collapsed?: boolean;
    /** Names the menu button for screen readers, e.g. "Actions for Acme Corp". */
    ariaLabel?: string;
    /**
     * A control drawn after the actions at every width, collapsed or not — a proposal row's field
     * history, which opens a popover of its own and carries a change badge, so it cannot be a menu item.
     */
    trailing?: ReactNode;
    /** Where the row opens. Given, the menu is always drawn, and Open is its first item. */
    open?: RowOpenAction;
  }>,
) {
  const { actions, ariaLabel, collapsed, open, trailing } = props;
  // Read through `useTheme` rather than the callback form of `useMediaQuery`: the callback is handed
  // null where no provider is mounted, and a shared component should not require one.
  const theme = useTheme();
  const viewportIsNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const isCollapsed = collapsed ?? viewportIsNarrow;

  if (actions.length === 0 && !open) return trailing ? <RowActionsContainer>{trailing}</RowActionsContainer> : null;

  const iconActions = isCollapsed ? [] : actions.filter((action) => !action.inMenu);
  const menuActions = isCollapsed ? actions : actions.filter((action) => action.inMenu);
  const openLabel = open?.label ?? "Open";

  return (
    <RowActionsContainer>
      {iconActions.map((action) => (
        <Tooltip key={action.key} title={describeAction(action)}>
          {/* A disabled button emits no pointer events, so the tooltip needs a live element under it. */}
          <span>
            <ActionButton
              aria-label={action.ariaLabel ?? action.label}
              // Colouring the button rather than the glyph tints the hover ripple with it, and
              // lets MRT's disabled styling win on its own — a `disabled` action greys out here
              // without the call site working the colour out for itself.
              color={action.destructive ? "error" : undefined}
              disabled={action.disabled}
              onClick={(event) => {
                // Rows are usually clickable themselves — acting on one must not also open it.
                event.stopPropagation();
                action.onClick(event.currentTarget);
              }}
            >
              {action.icon}
            </ActionButton>
          </span>
        </Tooltip>
      ))}
      {(menuActions.length > 0 || open) && (
        <>
          <ActionButton
            aria-label={ariaLabel ?? "Row actions"}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            onClick={(event) => {
              event.stopPropagation();
              setAnchorEl(event.currentTarget);
            }}
          >
            <MoreHorizIcon />
          </ActionButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
            onClick={(event) => event.stopPropagation()}
          >
            {open && (
              <ActionMenuItem
                // A route's Open is a real link, so the browser's own link gestures work on it.
                {...(open.to === undefined ? {} : ({ component: RouterLink, to: open.to } as object))}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  closeMenu();
                  open.onClick?.();
                }}
              >
                <ListItemIcon>
                  <OpenInBrowserIcon />
                </ListItemIcon>
                <ListItemText>{openLabel}</ListItemText>
              </ActionMenuItem>
            )}
            {menuActions.map((action) => (
              <ActionMenuItem
                key={action.key}
                aria-label={action.ariaLabel}
                disabled={action.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  const menuButton = anchorEl;
                  closeMenu();
                  if (menuButton) action.onClick(menuButton);
                }}
              >
                <ListItemIcon sx={action.destructive ? { color: "error.main" } : undefined}>{action.icon}</ListItemIcon>
                <ListItemText>{describeAction(action)}</ListItemText>
              </ActionMenuItem>
            ))}
          </Menu>
        </>
      )}
      {trailing}
    </RowActionsContainer>
  );
}
